/**
 * Місце діалогу швидкого створення в оболонці (DAT-01). У головному чанку — лише ця тонка
 * оболонка: клавіша `N` поза карткою (у картці `N` — нова нотатка, keyboard.md, розд. 1), команда
 * палітри «Швидко створити» і лінивий діалог (`create/QuickCreateDialog.tsx`). Лише після входу.
 */
import { useMatchRoute } from '@tanstack/react-router';
import { lazy, Suspense, useEffect } from 'react';
import { useHotkeys } from '../app/hotkeys.ts';
import { GLOBAL } from '../app/keymap.ts';
import { shell as st } from '../i18n/uk/shell.ts';
import { toast } from '../ui/notify/index.ts';
import { RetryBoundary } from '../ui/RetryBoundary.tsx';
import { closeQuickCreate, openQuickCreate, useQuickCreateRequest } from './quick-create.ts';
import { loadQuickCreate } from './quick-create-load.ts';
import { platformText as t } from './text.ts';

let QuickCreateDialog = lazy(loadQuickCreate);

/** Чанк діалогу не завантажився: тост замість порожнього екрана, наступне відкриття пробує знову. */
function LoadFailed() {
  useEffect(() => {
    QuickCreateDialog = lazy(loadQuickCreate);
    toast({ text: t.formUnavailable, tone: 'alert' });
    closeQuickCreate();
  }, []);
  return null;
}

export function QuickCreateHost({ signedIn }: { signedIn: boolean }) {
  const request = useQuickCreateRequest();
  const matchRoute = useMatchRoute();
  // Картка запису (не повна форма створення): там `N` належить картці.
  const onCard =
    Boolean(matchRoute({ to: '/r/$entity/$recordId', fuzzy: false })) &&
    !matchRoute({ to: '/r/$entity/new' });
  useHotkeys(
    signedIn
      ? [
          {
            ...(onCard ? {} : GLOBAL.quickCreate),
            label: st.quickCreate,
            handler: () => openQuickCreate(),
            command: { id: 'quick-create', group: 'command' },
          },
        ]
      : [],
    { scope: 'global' },
  );
  if (!request || !signedIn) return null;
  return (
    <RetryBoundary fallback={() => <LoadFailed />}>
      <Suspense fallback={null}>
        <QuickCreateDialog
          entity={request.entity}
          preset={request.preset}
          onClose={closeQuickCreate}
        />
      </Suspense>
    </RetryBoundary>
  );
}
